import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Download, Upload, Database, Check, Clock, FileText, Package } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

export default function DataExportPage() {
  const { t } = useLanguage();
  const [modules, setModules] = useState([]);
  const [history, setHistory] = useState([]);
  const [selected, setSelected] = useState([]);
  const [exportFormat, setExportFormat] = useState('json');
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [modRes, histRes] = await Promise.all([
        api.get('/data-export/modules'),
        api.get('/data-export/history'),
      ]);
      setModules(modRes.data);
      setHistory(histRes.data);
    } catch (err) {
      toast.error('Failed to load export data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const toggleModule = (key) => {
    setSelected(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const selectAll = () => {
    if (selected.length === modules.length) setSelected([]);
    else setSelected(modules.map(m => m.key));
  };

  const handleExport = async () => {
    if (selected.length === 0) { toast.error('Select at least one module'); return; }
    setExporting(true);
    try {
      const res = await api.post('/data-export/export', { modules: selected, format: exportFormat });
      const { data: exportData, format: fmt, total_records, exported_at } = res.data;

      if (fmt === 'csv') {
        for (const [modKey, csvContent] of Object.entries(exportData)) {
          const blob = new Blob([csvContent], { type: 'text/csv' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${modKey}_export_${exported_at.slice(0, 10)}.csv`;
          a.click();
          URL.revokeObjectURL(url);
        }
      } else {
        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ssc_export_${exported_at.slice(0, 10)}.json`;
        a.click();
        URL.revokeObjectURL(url);
      }

      toast.success(`Exported ${total_records} records from ${selected.length} modules`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        const text = await file.text();
        const json = JSON.parse(text);
        let totalImported = 0;
        for (const [modKey, records] of Object.entries(json)) {
          if (!Array.isArray(records) || records.length === 0) continue;
          const res = await api.post('/data-export/import', { module: modKey, records });
          totalImported += res.data.imported;
          toast.success(`${modKey}: ${res.data.imported} imported, ${res.data.skipped} skipped`);
        }
        toast.success(`Import complete: ${totalImported} total records`);
        fetchData();
      } catch (err) {
        toast.error('Import failed - invalid JSON file');
      }
    };
    input.click();
  };

  const totalRecords = modules.reduce((s, m) => s + m.record_count, 0);
  const selectedRecords = modules.filter(m => selected.includes(m.key)).reduce((s, m) => s + m.record_count, 0);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="data-export-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-4">

              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-200/50">

                <Download size={24} className="text-white" />

              </div>

              <div>

                <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="page-title">{t('data_export_title') || 'Data Export & Import'}</h1>

                <p className="text-sm text-muted-foreground mt-0.5">Export data in various formats</p>

              </div>

            </div>
            <p className="text-muted-foreground text-sm mt-1">Export tenant data as JSON/CSV or import from file.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleImport} data-testid="import-btn"><Upload size={14} className="mr-1.5" /> Import JSON</Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card data-testid="stat-modules"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-info/10 rounded-lg"><Package size={18} className="text-info" /></div><div><div className="text-2xl font-bold">{modules.length}</div><div className="text-xs text-muted-foreground">Modules</div></div></div></CardContent></Card>
          <Card data-testid="stat-records"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-success/10 rounded-lg"><Database size={18} className="text-success" /></div><div><div className="text-2xl font-bold">{totalRecords.toLocaleString()}</div><div className="text-xs text-muted-foreground">Total Records</div></div></div></CardContent></Card>
          <Card data-testid="stat-selected"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-warning/10 rounded-lg"><Check size={18} className="text-warning" /></div><div><div className="text-2xl font-bold">{selected.length}</div><div className="text-xs text-muted-foreground">Selected</div></div></div></CardContent></Card>
          <Card data-testid="stat-exports"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-purple-50 rounded-lg"><Clock size={18} className="text-purple-600" /></div><div><div className="text-2xl font-bold">{history.length}</div><div className="text-xs text-muted-foreground">Previous Exports</div></div></div></CardContent></Card>
        </div>

        <Tabs defaultValue="export" className="space-y-4">
          <TabsList><TabsTrigger value="export" data-testid="tab-export">Export</TabsTrigger><TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger></TabsList>

          <TabsContent value="export">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <CardTitle className="text-base">Select Modules to Export</CardTitle>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="ghost" onClick={selectAll} data-testid="select-all-btn">{selected.length === modules.length ? 'Deselect All' : 'Select All'}</Button>
                    <div className="flex border rounded-lg overflow-hidden">
                      <button className={`px-3 py-1.5 text-xs ${exportFormat === 'json' ? 'bg-primary text-white' : 'bg-white text-muted-foreground'}`} onClick={() => setExportFormat('json')} data-testid="fmt-json">JSON</button>
                      <button className={`px-3 py-1.5 text-xs ${exportFormat === 'csv' ? 'bg-primary text-white' : 'bg-white text-muted-foreground'}`} onClick={() => setExportFormat('csv')} data-testid="fmt-csv">CSV</button>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {modules.map((mod) => {
                    const isSelected = selected.includes(mod.key);
                    return (
                      <div
                        key={mod.key}
                        className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-border'}`}
                        onClick={() => toggleModule(mod.key)}
                        data-testid={`module-${mod.key}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${isSelected ? 'border-primary bg-primary' : 'border-border'}`}>
                              {isSelected && <Check size={10} className="text-white" />}
                            </div>
                            <span className="font-medium text-sm">{mod.label}</span>
                          </div>
                          <Badge variant="secondary" className="text-[10px]">{mod.record_count}</Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-6 gap-3">
                  <div className="text-sm text-muted-foreground">{selected.length} modules selected ({selectedRecords.toLocaleString()} records)</div>
                  <Button onClick={handleExport} disabled={exporting || selected.length === 0} data-testid="export-btn">
                    <Download size={14} className="mr-1.5" /> {exporting ? 'Exporting...' : `Export as ${exportFormat.toUpperCase()}`}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history">
            <Card>
              <CardHeader><CardTitle className="text-base">Export/Import History</CardTitle></CardHeader>
              <CardContent>
                {history.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">No export history yet.</div>
                ) : (
                  <div className="space-y-2">
                    {history.map((h) => (
                      <div key={h.id} className="border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2" data-testid={`history-${h.id}`}>
                        <div>
                          <div className="font-medium text-sm flex items-center gap-2">
                            {h.type === 'import' ? <Upload size={14} className="text-info" /> : <Download size={14} className="text-success" />}
                            {h.type === 'import' ? `Import: ${h.module}` : `Export: ${(h.modules || []).join(', ')}`}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">{h.exported_at || h.imported_at}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-xs">{h.total_records || h.imported_count || 0} records</Badge>
                          {h.format && <Badge variant="outline" className="text-[10px] uppercase">{h.format}</Badge>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
